import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { findNavItem } from './navSections';

/**
 * Stock Analyzer navigation.
 *
 * Purely presentational — it renders the section list and reports selections
 * upward. The dashboard remains the single owner of which tab is active.
 *
 * Desktop renders a vertical sidebar that collapses to an icon rail, because
 * this module's tables (Financials, Explorer, the spread heatmap) are very wide
 * and benefit from reclaiming the horizontal space. Below `lg` the sidebar
 * would leave no usable width, so it becomes a grouped dropdown instead.
 */
function StockAnalyzerSidebar({
  sections,
  activeTab,
  onSelect,
  collapsed,
  onToggleCollapsed,
}) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the mobile menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handlePointer = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  const handleSelect = (key) => {
    onSelect(key);
    setMenuOpen(false);
  };

  const itemClasses = (isActive) => [
    'w-full flex items-center gap-2.5 rounded-md text-sm font-medium',
    'border-l-2 transition-colors text-left',
    collapsed && isDesktop ? 'justify-center px-0 py-2.5' : 'px-3 py-2',
    isActive
      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
      : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-700/40',
  ].join(' ');

  // --- Mobile: a dropdown listing every tool, grouped by section ---
  if (!isDesktop) {
    const current = findNavItem(activeTab);
    return (
      <div className="relative mb-4" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg
                     bg-gray-800 border border-gray-700 text-white"
          aria-expanded={menuOpen}
          aria-haspopup="true"
        >
          <span className="flex items-center gap-2 font-medium">
            <span>{current?.icon}</span>
            {current?.label || 'Select a tool'}
          </span>
          <ChevronDown
            size={18}
            className={`text-gray-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {menuOpen && (
          <div
            className="absolute z-30 mt-2 w-full max-h-[70vh] overflow-y-auto rounded-lg
                       bg-gray-800 border border-gray-700 shadow-xl p-2"
          >
            {sections.map((section) => (
              <div key={section.title} className="mb-2 last:mb-0">
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold tracking-wider
                                text-gray-500 uppercase">
                  {section.title}
                </div>
                {section.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleSelect(item.key)}
                    className={itemClasses(activeTab === item.key)}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- Desktop: vertical sidebar, collapsible to an icon rail ---
  return (
    <aside
      className={`${collapsed ? 'w-14' : 'w-56'} shrink-0 transition-[width] duration-200`}
      aria-label="Stock Analyzer sections"
    >
      <nav className="bg-gray-800 rounded-lg p-2 sticky top-6">
        {sections.map((section) => (
          <div key={section.title} className="mb-3 last:mb-0">
            {/* The heading is the grouping cue; in the collapsed rail a divider
                stands in for it so the sections stay visually separated. */}
            {collapsed ? (
              <div className="h-px bg-gray-700 mx-2 my-2" aria-hidden="true" />
            ) : (
              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold tracking-wider
                              text-gray-500 uppercase">
                {section.title}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = activeTab === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleSelect(item.key)}
                    className={itemClasses(isActive)}
                    title={collapsed ? item.label : undefined}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span aria-hidden={collapsed ? undefined : 'true'}>{item.icon}</span>
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-3 pt-2 border-t border-gray-700">
          <button
            type="button"
            onClick={onToggleCollapsed}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs
                        text-gray-500 hover:text-gray-300 hover:bg-gray-700/40 transition-colors
                        ${collapsed ? 'justify-center px-0' : ''}`}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </nav>
    </aside>
  );
}

export default StockAnalyzerSidebar;
